<template>
    <div class="px-8 mb-8">
        <div
            class="
                -mx-4
                mt-8
                overflow-hidden
                shadow
                ring-1 ring-black ring-opacity-5
                sm:-mx-6
                md:mx-0
                md:rounded-lg
            "
        >
            <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                    <tr>
                        <th
                            scope="col"
                            class="
                                py-3.5
                                pl-4
                                pr-3
                                text-left text-sm
                                font-semibold
                                text-gray-900
                                sm:pl-6
                            "
                        >
                            Score
                        </th>
                        <th
                            scope="col"
                            class="
                                px-3
                                py-3.5
                                text-left text-sm
                                font-semibold
                                text-gray-900
                                lg:table-cell
                            "
                        >
                            Date
                        </th>

                        <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span class="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white">
                    <tr v-for="score in filteredScores" :key="score.id">
                        <td
                            class="
                                md:w-auto
                                max-w-0
                                py-4
                                pl-4
                                pr-3
                                text-sm
                                font-medium
                                text-gray-900
                                sm:w-auto
                                sm:max-w-none
                                sm:pl-6
                            "
                        >
                            {{ score.score }}
                        </td>
                        <td class="px-3 py-4 text-xs md:text-sm text-gray-500 sm:table-cell">
                            {{ score.score_date }}
                        </td>
                        <td class="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div class="space-x-1">
                                <a
                                    href="#"
                                    @click.prevent="
                                        setActiveScore(score);
                                        openCreditScoreModal();
                                    "
                                    class="
                                        inline-block
                                        text-gray-400
                                        hover:text-gray-500
                                        rounded-full
                                    "
                                >
                                    <span class="sr-only">Edit</span>
                                    <svg class="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                        <path
                                            d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z"
                                        />
                                    </svg>
                                </a>

                                <button
                                    type="submit"
                                    @click="handleDeleteClick(score)"
                                    class="text-red-500 hover:text-red-600 rounded-full"
                                >
                                    <span class="sr-only">Delete</span>
                                    <svg class="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                        <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                                        <path
                                            d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="flex justify-between pt-4" v-if="totalPages > 1">
            <button
                class="btn btn-primary ml-0"
                style="margin-left: 0"
                :class="{ disabled: currentPage === 1 }"
                @click.prevent="previous()"
                :disabled="currentPage === 1"
            >
                &larr; Prev
            </button>
            <button
                class="btn btn-primary"
                :class="{
                    disabled: currentPage === totalPages,
                }"
                @click.prevent="next()"
                :disabled="currentPage === totalPages"
            >
                Next &rarr;
            </button>
        </div>
        <ConfirmationDialog
            v-if="showDeleteScoreModal"
            :show="showDeleteScoreModal"
            title="Delete credit score entry"
            :dialog="`Are you about to delete the credit score entry from ${formatDate(activeScore?.score_date)}. This action cannot be undone.`"
            icon="trash"
            iconColor="red"
            :buttons="[
                { type: 'cancel', text: 'Cancel' },
                { type: 'confirm', text: 'Delete', value: true, classes: '!bg-red-500 text-white' }
            ]"
            @cancel="closeDeleteScoreModal"
            @confirm="confirmDeleteScore"
        >
            <template #title>
                <div class="flex items-center gap-3 self-stretch">
                    <div
                        class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                    >
                        <Icon icon="trash" pathClass="fill-[#EF4444]" />
                    </div>
                    <span
                        class="text-[var(--text-icons-primary,#1E293B)] font-inter text-xl font-medium leading-7"
                    >
                        Delete Credit Score Entry
                    </span>
                </div>
            </template>
        </ConfirmationDialog>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCreditScoreStore } from '@/store/creditscore';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    props: {},
    components: {
        ConfirmationDialog,
        Icon,
    },
    data: () => ({
        perPage: 10,
        currentPage: 1,
        startIndex: 0,
        endIndex: 10,
    }),
    directives: {},
    computed: {
        ...mapState(useCreditScoreStore, [
            'loadingResponse', 
            'userScoreData',
            'showDeleteScoreModal',
            'activeScore'
        ]),
        filteredScores() {
            let scores = this.userScoreData.all_scores;
            return scores.slice(this.startIndex, this.endIndex);
        },
        totalPages() {
            return Math.ceil(this.userScoreData.all_scores.length / this.perPage);
        },
    },
    methods: {
        ...mapActions(useCreditScoreStore, [
            'deleteScore',
            'setActiveScore',
            'openCreditScoreModal',
            'openDeleteScoreModal',
            'closeDeleteScoreModal',
        ]),
        closeCreditScoreModal() {},
        pagination(activePage) {
            this.currentPage = activePage;
            this.startIndex =
                this.activePage === 1 ? 0 : this.currentPage * this.perPage - this.perPage;
            this.endIndex = this.startIndex + this.perPage;
        },
        previous() {
            if (this.currentPage > 1) {
                this.pagination(this.currentPage - 1);
            }
        },
        next() {
            if (this.currentPage < this.totalPages) {
                this.pagination(this.currentPage + 1);
            }
        },
        handleDeleteClick(score) {
            this.setActiveScore(score);
            this.openDeleteScoreModal();
        },
        confirmDeleteScore() {
            if (this.activeScore) {
                this.deleteScore(this.activeScore);
                this.closeDeleteScoreModal();
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            
            // Parse the date (assuming input format is MM/DD/YYYY)
            const parts = dateString.split('/');
            if (parts.length !== 3) return dateString;
            
            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);
            
            // Array of month names
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];
            
            // Format as "Month Day, Year"
            return `${monthNames[month - 1]} ${day}, ${year}`;
        }
    },
};
</script>
